import { useState } from 'react';
import lodashCompact from 'lodash/fp/compact';

import { SUNLOCK_STATUS_ACTIVE } from 'common/sunlocks/sunlockConstants';
import { getDuration } from 'common/timeHelpers';

const useSunlockStatusController = ({
  sunlockStatus,
  sunlockSerial,
  sunlockLastPingDate,
  sunlockLogs,
  internetConnection,
  onFixSunlockConnectionClick,
  onSeeLogsClick,
}) => {
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);

  const _getStatusIcon = () => {
    if (sunlockStatus === SUNLOCK_STATUS_ACTIVE) {
      return '/images/checkmark-circle-green-no-shadow.svg';
    }
    return '/images/x-circle-icon-red.svg';
  };

  const _getStatusTitle = () => {
    if (sunlockStatus === SUNLOCK_STATUS_ACTIVE) {
      return 'Active Sunlock Status';
    }
    return `${sunlockStatus} Sunlock Status`;
  };

  const _getLastActiveConnectionText = () => {
    return internetConnection.lastActiveFor
      ? `for ${getDuration(internetConnection.lastActiveFor)}`
      : '';
  };

  const _getLatestPingText = () => {
    if (!sunlockLastPingDate) {
      return 'Never pinged';
    }

    const lastPingInterval = getDuration(sunlockLastPingDate);
    const sunlockIsActive = sunlockStatus === SUNLOCK_STATUS_ACTIVE;

    if (!lastPingInterval && !sunlockIsActive) {
      return 'Never pinged';
    }

    return lastPingInterval ? `Last pinged ${lastPingInterval} ago.` : '';
  };

  const _getDetails = () => {
    if (!sunlockSerial || !sunlockStatus) {
      return null;
    }

    const latestPingText = _getLatestPingText();

    if (sunlockStatus === SUNLOCK_STATUS_ACTIVE) {
      return latestPingText;
    }

    return lodashCompact([latestPingText, `Sunlock serial number: #${sunlockSerial}.`]).join(' ');
  };

  return {
    status: {
      icon: _getStatusIcon(),
      title: _getStatusTitle(),
      details: _getDetails(),
    },
    connectivity: {
      lastActiveFor: _getLastActiveConnectionText(),
      lastPingDate: getDuration(sunlockLastPingDate),
      shouldShowTroubleshooting: !!onFixSunlockConnectionClick,
    },
    logsModal: {
      logs: sunlockLogs,
      isOpen: isLogsModalOpen,
      open: () => setIsLogsModalOpen(true),
      close: () => setIsLogsModalOpen(false),
    },
    actions: {
      onFixSunlockConnectionClick,
      onSeeLogsClick,
    },
  };
};

export default useSunlockStatusController;
